import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import moment from 'moment'
import { toast } from 'react-toastify'
import { useAuth0 } from 'libs/auth0-react'
import { fontFamily } from 'theme'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  IconButton,
  Menu,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
} from '@mui/material'
import { ReactComponent as SquareLogo } from 'assets/pos/square-logo.svg'
import { ReactComponent as ToastLogo } from 'assets/pos/toast-logo.svg'
import { Button, Container, LoadingSkeleton } from 'components'
import ChilipiperSection from 'components/ChilipiperSection'
import { PaginationLinks } from 'api'
import { PromoCodeStatus } from 'api/promoCodes/enums'
import {
  formatUpdatePromoCodeStatusPayload,
  getPromoCodes,
  updatePromoCodeStatus,
} from 'api/promoCodes/promoCodes'
import { Meta, PromoCode } from 'api/promoCodes/types'
import { useGetLocationPartners, useGetLocations, useGetOrganizations } from 'hooks'
import { getFeatureVisibilityByPOS, getPromoCodesNewURL, getPromoCodesURL } from 'utils'
import { formatPromoDescription, getPromoCodeLabel } from './utils'
import Header from './Header/Header'
import PromoCodesModal from './Modals/PromoCodesModal'
import PromoCodesDrawer from './PromoCodesDrawer'
import SingleOrganizationDropdown from 'components/SingleOrganizationDropdown/SingleOrganizationDropdown'
import UnsupportedFeatureSplash from 'components/UnsupportedFeatureSplash/UnsupportedFeatureSplash'
import * as Styled from './styles'


export const PromoCodes = () => {
  const { user } = useAuth0()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams() 
  const { defaultLocation } = useGetLocations()
  const { defaultOrganization, setDefaultOrganizationAndUpdateURL } = useGetOrganizations()
  const [isAllowed, setIsAllowed] = useState(false)
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('')
  const partnerId = defaultLocation?.id ? String(defaultLocation.id) : null
  const { organization, partner } = useGetLocationPartners(partnerId ?? undefined)
  const isPlatformOrganization = organization?.attributes?.chowly_platform ?? false
  const isAdminUser = user?.email?.includes('@chowlyinc.com')
  const { showPromoCodes } = partner ? getFeatureVisibilityByPOS(partner) : { showPromoCodes: false }
  const posType = partner?.attributes?.pos ?? ''

  useEffect(() => {
    const checkAccess = () => {
      if (isPlatformOrganization || isAdminUser) {
        setIsAllowed(true)
      }
    }
    checkAccess()
  }, [isPlatformOrganization, isAdminUser])

  // Select organization logic (extract and centralize it)
  const selectOrganization = useCallback(
    (id: string) => {
      setSelectedOrganizationId(id)
      setDefaultOrganizationAndUpdateURL(id, (orgId) => getPromoCodesURL(orgId))
    },
    [setDefaultOrganizationAndUpdateURL]
  )

  // Sync organization from URL or fallback
  useEffect(() => {
    const orgIdFromURL = searchParams.get('orgId')
    const orgIdByDefaultLocation = defaultLocation?.organization_id

    // Check if there is an orgId in the URL
    if (orgIdFromURL) {
      selectOrganization(orgIdFromURL)
    } 
    // If there is no orgId, we give priority to the default location's organization
    else if (orgIdByDefaultLocation) {
      selectOrganization(String(orgIdByDefaultLocation))
    }
    // If no orgId in the URL, but there is a defaultOrganization, use it
    else if (defaultOrganization?.id) {
      selectOrganization(String(defaultOrganization.id))
    }
  }, [searchParams, defaultLocation?.organization_id, defaultOrganization?.id, selectOrganization])

  // Handle organization change from the dropdown
  const handleOrganizationChange = (id: number) => {
    selectOrganization(String(id))
  }

  if (!partner) return <LoadingSkeleton lineCount={3} />
  
  return (
    <>
      <Header title='Promo Codes' />
      <Container>
        <Styled.DropdownContainer>
          <SingleOrganizationDropdown onOrganizationChange={handleOrganizationChange} />
        </Styled.DropdownContainer>
        {isAllowed && showPromoCodes ? (
          <Styled.PromoCodesContainer>
            <Styled.RowPair style={{ marginBottom: 20 }}>
              <Styled.PromoCodesTitle>Coupons</Styled.PromoCodesTitle>
              <Button size='small' narrow onClick={() => navigate(getPromoCodesNewURL())}>
                Create
              </Button>
            </Styled.RowPair>
            <TableComponent organizationId={selectedOrganizationId} />
          </Styled.PromoCodesContainer>
        ) : (
          <>
            <Styled.ChilipiperSectionWrapper>
              <ChilipiperSection
                message='Promo codes are available to platform users. Upgrade now to unlock this feature.'
                actionBtnText='Reach out to sales to learn how you can upgrade to a Platform Plan'
              />
            </Styled.ChilipiperSectionWrapper>
            <UnsupportedFeatureSplash
              title={`Promo Codes setup happens in your ${posType} dashboard`}
              description={`Since you're using ${posType} as your POS system, loyalty program configuration needs to be managed directly through your ${posType} account. Once configured there, your settings will automatically sync with Chowly.`}
              icon={posType === 'Toast' ? <ToastLogo style={{ width: 64, height: 64 }} /> : <SquareLogo style={{ width: 64, height: 64 }} />}
              onClick={() => window.open(posType === 'Toast' ? 'https://toasttab.com/dashboard' : 'https://squareup.com/dashboard', '_blank')}
              buttonText={`Open ${posType} Dashboard`}
              features={[
                {
                  title: `1. Configure in ${posType}`,
                  description: `Set up your promo codes settings directly in your ${posType} dashboard.`,
                },
                {
                  title: '2. Automatic Sync',
                  description: 'Your promo codes program data will automatically appear in Chowly within a few minutes.',
                },
                {
                  title: '3. View Status',
                  description: 'Once synced, you\'ll be able to view and monitor your promo codes performance right here in Chowly.',
                },
              ]}
            />
          </>
        )}
      </Container>
    </>
  )
}

export const TableComponent = ({ organizationId }: { organizationId: string }) => {
  const [rowsPerPage, setRowsPerPage] = useState(15)
  const [page, setPage] = useState(0)
  const [data, setData] = useState<PromoCode[] | []>([])
  const [link, setLink] = useState<PaginationLinks>({
    first: null,
    prev: null,
    self: null,
    next: null,
    last: null,
  })
  const [meta, setMeta] = useState<Meta>({
    limit: `${rowsPerPage}`,
    page: 1,
    page_total: 1,
    total: 0,
  })
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedRow, setSelectedRow] = useState<number | null>(null)
  const [selectedPromoCode, setSelectedPromoCode] = useState<PromoCode | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const fetchPromoCodes = useCallback(
    async (link?: string) => {
      try {
        if (!organizationId) return
        const response = await getPromoCodes({ organizationId, link, limit: rowsPerPage })
        const promoCodesData = response?.data.map((promoCodeResource) => {
          const attrs = promoCodeResource.attributes
          return {
            ...attrs,
            description: formatPromoDescription(attrs.discount_amount, attrs.discount_type),
          }
        })
        setData(promoCodesData)
        if (response?.links) {
          setLink(response.links)
        }
        if (response?.meta) {
          setMeta(response.meta as Meta)
        }
      } catch (error) {
        console.error('Failed to fetch promo codes:', error)
      } finally {
        setIsLoading(false)
      }
    },
    [organizationId, rowsPerPage],
  )

  useEffect(() => {
    if (!organizationId) return
    setIsLoading(true)
    fetchPromoCodes()
  }, [organizationId, rowsPerPage])

  const handleChangePage = (event: any, newPage: number) => {
    if (newPage > page && link.next) {
      fetchPromoCodes(link.next)
    } else if (newPage < page && link.prev) {
      fetchPromoCodes(link.prev)
    } else {
      fetchPromoCodes(link.self || undefined)
    }
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: any) => {
    const newRowsPerPage = parseInt(event.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setPage(0)
  }

  const handleRowClick = (_event: React.MouseEvent<HTMLTableRowElement>, promoCode: PromoCode) => {
    if ((_event.target as HTMLElement).closest('[data-testid="more-button"]')) {
      return
    }
    setSelectedPromoCode(promoCode)
    setIsDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false)
    setSelectedPromoCode(null)
  }

  const handleModalOpen = (promoCode: PromoCode) => {
    setSelectedPromoCode(promoCode)
    setIsModalOpen(true)
  }

  const handleModalClose = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation()
    setIsModalOpen(false)
    setSelectedPromoCode(null)
  }

  const handleCancelCoupon = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (selectedPromoCode) {
      try {
        if (!organizationId) {
          toast.error('Organization ID is required.')
          return
        }
        if (!selectedPromoCode.id) {
          toast.error('Coupon ID is required.')
          return
        }

        const updatePromoCodeStatusPayload = formatUpdatePromoCodeStatusPayload({ rules: {}, active: false })
        await updatePromoCodeStatus(
          organizationId,
          selectedPromoCode.id,
          updatePromoCodeStatusPayload,
        )
        toast.success('Coupon has been successfully canceled.', { autoClose: 5000 })
        setIsModalOpen(false)
        setSelectedPromoCode(null)
        fetchPromoCodes()
      } catch (error) {
        toast.error('Failed to cancel coupon status.')
        console.error('Error updating promo code status:', error)
      }
    }
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, rowIndex: number) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setSelectedRow(rowIndex)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedRow(null)
  }

  return isLoading ? (
    <LoadingSkeleton lineCount={3} />
  ) : (
    <Container>
      <TableContainer>
        <Table>
          <TableHead>
            <Styled.StyledTableRow>
              <Styled.MobileVisibleTableHeader>Coupon Code</Styled.MobileVisibleTableHeader>
              <Styled.CustomTableCell>Created</Styled.CustomTableCell>
              <Styled.CustomTableCell>Start date</Styled.CustomTableCell>
              <Styled.CustomTableCell>End date</Styled.CustomTableCell>
              <Styled.MobileVisibleTableHeader>Redeemed</Styled.MobileVisibleTableHeader>
              <Styled.CustomTableCell>Status</Styled.CustomTableCell>
              <Styled.CustomTableCell>Actions</Styled.CustomTableCell>
            </Styled.StyledTableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 ? (
              data.map((row, index) => {
                return (
                  <Styled.StyledTableRow
                    key={row.id}
                    onClick={(event) => handleRowClick(event, row)}
                  >
                    <Styled.MobileVisibleTableCell>
                      <Styled.CouponCodeText>{row.code}</Styled.CouponCodeText>
                      <Styled.DescriptionText>{row.description}</Styled.DescriptionText>
                    </Styled.MobileVisibleTableCell>
                    <Styled.CustomTableCell>
                      {moment(row.created).format('MM/DD/YYYY')}
                    </Styled.CustomTableCell>
                    <Styled.CustomTableCell>
                      {moment(row.starts_at).format('MM/DD/YYYY')}
                    </Styled.CustomTableCell>
                    <Styled.CustomTableCell>
                      {row.expires_at ? moment(row.expires_at).format('MM/DD/YYYY') : '-'}
                    </Styled.CustomTableCell>
                    <Styled.CustomTableCell>{row.redeemed}</Styled.CustomTableCell>
                    <Styled.CustomTableCell>
                      <Styled.StyledChip
                        label={getPromoCodeLabel(row.aasm_state)}
                        status={row.aasm_state}
                        size='small'
                      />
                    </Styled.CustomTableCell>
                    {row.aasm_state !== PromoCodeStatus.DISABLED && (
                      <Styled.CustomTableCell>
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, index)}
                          data-testid='more-button'
                          sx={{ borderRadius: '4px', width: '28px', height: '28px' }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={selectedRow === index}
                          onClose={handleMenuClose}
                        >
                          <Styled.MenuItems
                            onClick={(event) => {
                              event.stopPropagation()
                              handleModalOpen(row)
                              handleMenuClose()
                            }}
                          >
                            Cancel
                          </Styled.MenuItems>
                        </Menu>
                      </Styled.CustomTableCell>
                    )}
                  </Styled.StyledTableRow>
                )
              })
            ) : (
              <Styled.StyledTableRow>
                <Styled.CustomTableCell colSpan={7} align='center'>
                  No data available
                </Styled.CustomTableCell>
              </Styled.StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Styled.PaginationContainer>
        <TablePagination
          component='div'
          count={meta.total}
          page={page}
          onPageChange={handleChangePage}
          slotProps={{
            actions: {
              previousButton: {
                disabled: !link?.prev,
              },
              nextButton: {
                disabled: !link?.next,
              },
            },
          }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage='Rows per page'
          rowsPerPageOptions={[5, 10, 15, 20, 25]}
          sx={{
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-caption, & .MuiTablePagination-input, & .MuiTablePagination-select':
              {
                fontFamily: fontFamily.inter,
                fontSize: 13,
              },
          }}
        />
      </Styled.PaginationContainer>

      <PromoCodesDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        promoCode={selectedPromoCode}
        onModalOpen={handleModalOpen}
      />

      {selectedPromoCode && (
        <PromoCodesModal
          isOpen={isModalOpen}
          title={`Cancel Coupon ${selectedPromoCode.code}?`}
          description={`This action can't be undone, make sure you want to cancel this coupon?`}
          submitText={'Cancel Coupon'}
          onClose={handleModalClose}
          onCancelCoupon={handleCancelCoupon}
          promoCode={selectedPromoCode}
        />
      )}
    </Container>
  )
}

export default PromoCodes
